import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ProductPayloadService } from '../../product-payload/product-payload.service';
import { mergeMap, tap } from 'rxjs/operators';
import { dinero, toSnapshot, toUnit } from 'dinero.js';
import { EMPTY, Observable } from 'rxjs';
import { Product } from '../../product';
import { ProductFormComponent } from '../../product-form/product-form.component';
import { CAD } from '@dinero.js/currencies';
import { StoreService } from '../../../store/store.service';
import { ProductService } from '../../product.service';
import { Image } from '../../../shared/image-upload/image';
import { Category } from '../../../main-shared/categories-display/category';
import { CategoryService } from '../../../main-shared/categories-display/category.service';

@Component({
  selector: 'app-edit-product-form',
  templateUrl: './edit-product-form.component.html',
  styleUrls: ['./edit-product-form.component.scss'],
})
export class EditProductFormComponent implements OnInit {
  @Input() product: Product | undefined;
  @Output() updatedProduct = new EventEmitter<Product>();
  @ViewChild(ProductFormComponent)
  productFormComponent: ProductFormComponent | undefined;
  updateProductForm = this.fb.group({
    product: this.fb.nonNullable.control({
      name: '',
      description: '',
      free: false,
      price: 0,
      imagesInput: new Array<Image>(),
      filesInput: new Array<File>(),
    }),
  });
  submitted = false;
  categories$: Observable<Category[]> | undefined;
  filenames$: Observable<string[]> | undefined;
  buttonLabel = 'Mettre à jour';
  isSubmitDisabled = false;

  constructor(
    private readonly storeService: StoreService,
    private readonly productPayloadService: ProductPayloadService,
    private readonly categoryService: CategoryService,
    private readonly productService: ProductService,
    private fb: FormBuilder,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateProductForm.valueChanges.subscribe(() => {
      this.updateSubmitDisabled();
    });
    this.categories$ = this.getCategories$();
    this.filenames$ = this.productPayloadService.listPayloadFiles(
      this.product?.id!
    );
    this.productPayloadService.currentProductID = this.product?.id!;
  }

  updateSubmitDisabled() {
    this.isSubmitDisabled = this.updateProductForm.invalid || this.submitted;
    this.cd.detectChanges();
  }

  @ViewChild('productForm') set productForm(
    formComponent: ProductFormComponent
  ) {
    if (formComponent) {
      this.productFormComponent = formComponent;
      this.preselectCategories();
    }
  }

  submitUpdatedProduct() {
    if (this.updateProductForm.valid && this.productFormComponent) {
      this.submitted = true;
      const productForm = this.updateProductForm.controls.product.value;
      let price = productForm.price * 100;
      if (productForm.free) {
        price = 0;
      }
      const images: Image[] = productForm.imagesInput.filter(
        (image: Image) => image.data != ''
      );
      const imageLocations: Image[] = productForm.imagesInput.filter(
        (image: Image) => !image.location.includes('data:image/')
      );
      const productToUpdate: Product = {
        id: this.product?.id,
        name: productForm.name,
        description: productForm.description,
        images: images.map((image: Image) => image.data),
        images_location: imageLocations.map((image: Image) => image.location),
        price: toSnapshot(
          dinero({
            amount: price,
            currency: CAD,
          })
        ).amount,
        categories: this.productFormComponent.getSelectedCategories(),
      };
      const filesInput = productForm.filesInput;
      this.storeService
        .updateStoreProduct(productToUpdate)
        .pipe(
          tap((updatedProduct) => this.updatedProduct.emit(updatedProduct)),
          tap(() => (this.submitted = false)),
          tap(() => (this.categories$ = this.getCategories$())),
          mergeMap((updatedProduct) =>
            filesInput.length > 0
              ? this.productPayloadService.updateProductPayload({
                  files: filesInput,
                  product: updatedProduct.id!,
                })
              : EMPTY
          )
        )
        .subscribe(() => {});
    }
  }

  private preselectCategories(): void {
    if (this.product?.categories && this.product.categories.length > 0) {
      this.productFormComponent?.setSelectedCategories(this.product.categories);
    }
  }

  private setFormValues() {
    if (this.product) {
      let free = false;
      if (this.product.price == 0) {
        free = true;
      }
      const productImage = this.productService
        .getProductImagesUrlsCacheBreaking(this.product)
        .map((location) => <Image>{ location: location, data: '' });
      const formValues = {
        name: this.product.name,
        description: this.product.description,
        free: free,
        price: toUnit(
          dinero({
            amount: this.product.price,
            currency: CAD,
          })
        ),
        imagesInput: productImage,
        filesInput: [],
      };
      this.updateProductForm.controls.product.setValue(formValues);
    }
  }

  private getCategories$(): Observable<Category[]> {
    return this.categoryService
      .getAllCached()
      .pipe(tap(() => this.setFormValues()));
  }
}
